import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

/**
 * Compliance mode tenancy — controls which modules are visible.
 * Values: 'both' (default), 'nis2_only', 'ai_act_only'
 */

const ComplianceModeContext = createContext({
  mode: 'both',
  companyName: 'Empresa',
  isLoading: true,
  showNis2: true,
  showAiAct: true,
  refresh: async () => {},
});

export const useComplianceMode = () => useContext(ComplianceModeContext);

export const ComplianceModeProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [mode, setMode] = useState('both');
  const [companyName, setCompanyName] = useState('Empresa');
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setMode('both');
      setIsLoading(false);
      return;
    }
    try {
      const res = await api.get('/settings/compliance-mode');
      setMode(res.data.compliance_mode || 'both');
      setCompanyName(res.data.company_name || 'Empresa');
    } catch (_) {
      setMode('both');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  const showNis2 = mode !== 'ai_act_only';
  const showAiAct = mode !== 'nis2_only';

  return (
    <ComplianceModeContext.Provider
      value={{ mode, companyName, isLoading, showNis2, showAiAct, refresh: load }}
    >
      {children}
    </ComplianceModeContext.Provider>
  );
};

export default ComplianceModeProvider;
